<template>
    <div class="col-lg-8 mx-auto">
        <header class="text-center">
            <workflow-navigation workflowStage="DB Options" />
            <div class="col-lg-10 mx-auto">
                <h2 class="skin-page-heading">
                    Your pension<span v-if="schemeInfo"> in the {{ schemeInfo.name }}</span>
                </h2>
                <p>{{ templateText('dboptions.subtitle.text', { retirementDate }) }}</p>
            </div>
        </header>

        <div class="accordion mt-4 mb-5">
            <div v-if="fsOptionsDynamic">
                <DBOptionsAccordionItem :position="1" :open="openAccordionIndexes.includes(accordionSteps.options)" :title="`Your options in the ${schemeInfo.name}`"
                                        :isNotLastItem="true" @toggle-accordion-item="toggleAccordionItem(accordionSteps.options)">
                    <options-summary :scheme="schemeInfo" :optionsDynamic="availablePensionOptions" :availableOptionsStatic="availablePensionOptionsStatic" />
                </DBOptionsAccordionItem>

                <DBOptionsAccordionItem :position="2" :open="openAccordionIndexes.includes(accordionSteps.important)" title="Important information to consider"
                                        :isNotLastItem="true" @toggle-accordion-item="toggleAccordionItem(accordionSteps.important)">
                    <important-information :scheme="schemeInfo" />
                </DBOptionsAccordionItem>

                <DBOptionsAccordionItem :position="3" :open="openAccordionIndexes.includes(accordionSteps.chart)" :title="templateText('dboptions.modelling.title', { schemeName: schemeInfo.name })"
                                        :isNotLastItem="true" @toggle-accordion-item="toggleAccordionItem(accordionSteps.chart)">
                    <modelling
                        v-model:selected-option-name="selectedOptionName"
                        :availableOptionsStatic="availablePensionOptionsStatic"
                        :taxFreeCashModelSupporter="taxFreeCashModelSupporter"
                        :fsOptionsDynamic="fsOptionsDynamic"
                        :avc-pot-amount="finalSalaryAsset.avcPotAmount"
                        v-model:selected-option-starting-pension="selectedOptionStartingPension" />
                </DBOptionsAccordionItem>

                <DBOptionsAccordionItem :position="4" :open="openAccordionIndexes.includes(accordionSteps.next)" title="Next steps"
                                        :isNotLastItem="false" @toggle-accordion-item="toggleAccordionItem(accordionSteps.next)">
                    <next-steps
                        :scheme="schemeInfo"
                        :taxFreeCashModelSupporter="taxFreeCashModelSupporter"
                        :selectedOptionName="selectedOptionName"
                        v-model:immediate-spend-model="immediateSpendModel"
                        :selected-option-starting-pension="selectedOptionStartingPension"
                        @update-selected-next-steps-option="updateSelectedNextStepsOption" />
                </DBOptionsAccordionItem>
            </div>
            <div v-else class="loading-content text-center">
                <loading-spinner />
                <p><span class="fs-3">Retrieving your information.</span></p>
            </div>
        </div>

        <bad-response-viewer ref="badResponseViewer" />

        <contact-us class="mb-5"></contact-us>

        <page-nav back-link="requirements" @next-clicked="onNextClicked" :parent-will-handle-next="true" next-link="haves" />
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import BadResponseViewer from "@/shared/components/BadResponseViewer.vue";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";
import FinalSalaryOption from "@/guiide/ts/assets/finalsalary/finalSalaryOption";
import { app } from "../ts/app";
import { useStore } from "vuex";
import { FinalSalary } from "../ts/finalSalary";
import FinalSalaryScheme from "../ts/assets/finalsalary/finalSalaryScheme";
import LoadingSpinner from "@/shared/components/LoadingSpinner.vue";
import { FinalSalaryOptionInfoStatic } from "@/guiide/ts/assets/finalsalary/finalSalaryOptionInfoStatic";
import FinalSalarySchemeOptionsDetailResponse from "@/guiide/ts/response/finalSalarySchemeOptionsDetailResponse";
import FinalSalarySchemeOptionsDetail from "@/guiide/ts/response/finalSalarySchemeOptionsDetail";
import OptionsSummary from "@/guiide/components/dboptions/OptionsSummary.vue";
import ImportantInformation from "@/guiide/components/dboptions/ImportantInformation.vue";
import Modelling from "@/guiide/components/dboptions/Modelling.vue";
import NextSteps from "@/guiide/components/dboptions/NextSteps.vue";
import TaxFreeCashModelSupporter from "@/guiide/ts/assets/finalsalary/taxFreeCashModelSupporter";
import DBOptionsAccordionItem from "@/guiide/components/dboptions/DBOptionsAccordionItem.vue";
import type { NextStepsOptions } from '@/guiide/ts/dbOptions/nextStepsOptions';
import { formatter } from "@/shared/ts/formatter";
import { useFinalSalaryStaticOptions } from "../ts/assets/finalsalary/useFinalSalaryOptionInfoStatic";
import { useUIContent } from "../ts/configuration/useUIContent";

const store = useStore();
const { templateText } = useUIContent();

const nextLink = ref("");
const taxFreeCashModelSupporter = reactive<TaxFreeCashModelSupporter>(new TaxFreeCashModelSupporter());
const selectedOptionName = ref("");
const immediateSpendModel = ref(0);
const selectedNextStepsOption = ref<NextStepsOptions>();
const badResponseViewer = ref(null);
const accordionSteps = { options: 0, important: 1, chart: 2, next: 3, last: 3 };
const openAccordionIndexes = ref<number[]>([0]);
const previouslyOpenedAccordionIndexes = ref<number[]>([0]);
const schemeInfo = ref<FinalSalaryScheme | null>(null);
const finalSalaryAsset = ref<FinalSalary | null>(null);
const retirementDate = ref("");
const selectedOptionStartingPension = ref<number>(0);

const { finalSalaryOptionsStatic } = useFinalSalaryStaticOptions();
const fsOptionsDynamic = ref<FinalSalarySchemeOptionsDetailResponse>(null);

onMounted(async () => {
    const nextWorkflowStep = await workflow.getNextWorkflowStep(View.DBOptions);

    if (!nextWorkflowStep) {
        throw new Error('Failed to get next workflow step');
    }

    nextLink.value = nextWorkflowStep.resource;

    await updateModelValuesFromRetiree();
});

watch(
    () => store.state.retiree,
    async () => {
        await updateModelValuesFromRetiree();
    }
);

watch(
    () => taxFreeCashModelSupporter.activeActual,
    (newValue: number) => {
        if (newValue < immediateSpendModel.value) {
            immediateSpendModel.value = newValue;
        }
    }
);

const canProceedToNextStep = computed<boolean>(() => previouslyOpenedAccordionIndexes.value.includes(accordionSteps.last));

const availablePensionOptions = computed<FinalSalarySchemeOptionsDetail[]>(() => fsOptionsDynamic.value?.options.filter((fsoi: FinalSalarySchemeOptionsDetail) => fsoi.show === true) ?? []);
const showOptionMap = computed<Map<FinalSalaryOption, boolean>>(() => new Map(fsOptionsDynamic.value?.options.map((item: FinalSalarySchemeOptionsDetail) => [item.type, item.show]) ?? []));
const availablePensionOptionsStatic = computed<FinalSalaryOptionInfoStatic[]>(() => finalSalaryOptionsStatic.value.getAllOptions().filter((item: FinalSalaryOptionInfoStatic) => showOptionMap.value.get(item.type)));

const selectedOption = computed<FinalSalaryOption>(() => finalSalaryOptionsStatic.value.optionFromString(selectedOptionName.value));

watch(
    () => selectedOption.value,
    (newValue: FinalSalaryOption) => {
        taxFreeCashModelSupporter.changeActive(newValue);
        if (immediateSpendModel.value > taxFreeCashModelSupporter.activeMax) {
            immediateSpendModel.value = taxFreeCashModelSupporter.activeMax;
        }
    }
);

async function onNextClicked() {
    badResponseViewer.value.clear();

    if (!canProceedToNextStep.value) {
        badResponseViewer.value.update(false, "Please expand all of the above sections before proceeding.");
        return;
    }

    if (selectedNextStepsOption.value != "buildPlan") {
        badResponseViewer.value.update(false, "Please choose the 'I want to build a retirement plan' option in the final section to proceed.");
        return;
    }
    
    const taxFreeCashLumpSumAmount = taxFreeCashModelSupporter.getActual(selectedOption.value);
    if (taxFreeCashLumpSumAmount == null || Number.isNaN(taxFreeCashLumpSumAmount)) {
        throw new Error('Failed to get tax-free lump sum amount');
    }

    const immediateSpendAmount = immediateSpendModel.value;

    const result = await app.updateFinalSalaryOption(
        selectedOption.value, 
        taxFreeCashLumpSumAmount, 
        immediateSpendAmount);

    if (result.successful) {
        await workflow.goToNextWorkflowStep();
    } else {
        badResponseViewer.value.update(false, result.message);
    }
}

async function updateModelValuesFromRetiree() {
    if (!store.state.retiree) {
        return;
    }

    if (fsOptionsDynamic.value) {
        return;
    }

    retirementDate.value = formatter.monthAndYearDate(store.state.retiree.retirementDate);
    schemeInfo.value = store.state.retiree.finalSalaries.schemes[0];
    finalSalaryAsset.value = store.state.retiree.finalSalaries.assets[0];

    const fsOptions: FinalSalarySchemeOptionsDetailResponse = await app.getFinalSalaryOptions(schemeInfo.value.id);

    const taxFreeCashSliderInterval = fsOptions.taxFreeCashSliderInterval;
    taxFreeCashModelSupporter.setOptions(fsOptions.options, FinalSalaryOption.SchemeDefault, taxFreeCashSliderInterval);

    const schemeFinalSalary: FinalSalary = store.state.retiree.finalSalaries.assets?.find((fs: FinalSalary) => fs.option != null);
    if (schemeFinalSalary?.option == FinalSalaryOption.PensionIncreaseExchange) {
        selectedOptionName.value = finalSalaryOptionsStatic.value.pensionIncreaseExchange.name;
    } else if (schemeFinalSalary?.option == FinalSalaryOption.BridgingPension) {
        selectedOptionName.value = finalSalaryOptionsStatic.value.bridgingPension.name;
    } else {
        selectedOptionName.value = finalSalaryOptionsStatic.value.standard.name;
    }

    taxFreeCashModelSupporter.selected = schemeFinalSalary?.taxFreeCashAmount ?? 0;
    taxFreeCashModelSupporter.refresh();

    immediateSpendModel.value = schemeFinalSalary?.taxFreeCashImmediateSpendAmount ?? 0;

    fsOptionsDynamic.value = fsOptions;
}

function toggleAccordionItem(index: number) {
    const isOpen = openAccordionIndexes.value.includes(index);

    const isFirstItem = index === 0;
    const previousStepIsOpen = openAccordionIndexes.value.includes(index - 1);
    const wasPreviouslyOpen = previouslyOpenedAccordionIndexes.value.includes(index - 1);

    if (!isOpen) {
        if (isFirstItem || previousStepIsOpen || wasPreviouslyOpen) {
            openAccordionIndexes.value.push(index);
            previouslyOpenedAccordionIndexes.value.push(index);
        }
    } else {
        openAccordionIndexes.value = openAccordionIndexes.value.filter(i => i !== index);
    }
}

function updateSelectedNextStepsOption(newValue: NextStepsOptions) {
    selectedNextStepsOption.value = newValue;
}
</script>

<style lang="css">
    .radio-group {
        display: flex;
        width: 100%;
    }
    .radio-option {
        flex: 1;
        cursor: pointer;
        border: 2px solid #ccc;
        padding: 0.75rem;
        background-color: transparent;
        transition: all 0.3s ease;
        display: flex;
        align-items: stretch;
        justify-content: center;
    }
    .radio-option.active {
        color: white;
        border-color: #999;
    }
    .radio-option.active p {
        color: white !important;
    }
    .radio-option input[type="radio"] {
        display: none;
    }
    .radio-option .content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        text-align: center;
    }
    .radio-option i {
        font-size: 3rem;
    }

    .accordion {
        border-radius: 15px;
    }

    .loading-content {
        background: #fff;
        width: 100%;
        border: none;
        font-weight: bold;
        padding: 8rem;
    }
</style>
